import { Button, Col, ModalV2, P4PDayGiftBadge, Row, Text } from 'components';
import { Api, getP4PDayBadges, theme } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

const Code = styled.span`
  font-family: monospace;
  font-size: 18px;
  letter-spacing: 2px;
  white-space: nowrap;
  background-color: #f2f4f7;
  border-radius: 6px;
  padding: 4px 10px;
`;

/** One selectable colour in the award view. */
const Option = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0 0 10px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 2px solid ${(props) => (props.selected ? props.accent : '#e4e7ec')};
  background-color: ${(props) => (props.selected ? '#f9fafb' : '#ffffff')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  text-align: left;
`;

/** Keeps a timestamp on one line, and its digits from jittering as the clock ticks. */
const DateValue = styled.span`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-size: 14px;
  color: ${(props) => props.col};
`;

/**
 * Seconds are dropped deliberately: the full `toLocaleString` ran past the panel width and
 * wrapped the timestamp onto a second line, and nobody reconciling a booth handover needs them.
 */
function fmt(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

/**
 * Which client the fan enrolled through.
 *
 * Should read "App" for every badge issued from now on — the server refuses a web enrolment.
 * "Web" therefore means one of two things, both worth seeing: the fan was enrolled during the
 * window when the CMS still awarded badges, or the gate has stopped holding. Blank for anyone
 * enrolled before the field existed.
 */
function platformLabel(value, awardedBy) {
  // Issued by hand from this panel rather than claimed by the fan on any client.
  if (awardedBy) return 'Admin';
  if (value === 'app') return 'App';
  if (value === 'web') return 'Web';
  return '—';
}

const Field = ({ label, children }) => (
  <Row noFlex between wid="100%" center marg="0 0 14px">
    <Text med col={theme.GREY_60} marg="0 16px 0 0" style={{ whiteSpace: 'nowrap' }}>
      {label}
    </Text>
    {children}
  </Row>
);

/**
 * P4P Day booth handover for one fan.
 *
 * The distinction this screen exists to make: a fan holds a BADGE from the moment they enrol,
 * which is why `awardedAt` is always the same instant as `enrolledAt` and tells staff nothing.
 * `giftClaimed` is the physical handover at the stadium booth — the only field that answers
 * whether this person is still owed a prize.
 *
 * Rendered from UserPanel rather than from the settings dropdown, and its `visible` state lives
 * there too — the same arrangement Verify uses, and for the same reason. ContentHeader receives
 * `SettingsComponent` as a fresh arrow function on every render, so React sees a new component
 * type and remounts the whole Settings subtree, wiping any state held inside it. Anything that
 * has to survive an unrelated re-render cannot live in there.
 */
/**
 * Whether an admin may award this fan a badge by hand: they never enrolled, or an admin revoked
 * their badge. A fan whose own claim found no stock has a null colour too but no `revokedAt`,
 * and stays ineligible — the server applies the same rule.
 */
export function canAwardP4PDay(user) {
  const p4pDay = user?.p4pDay;
  return !p4pDay || (!!p4pDay.revokedAt && !p4pDay.giftColor);
}

const P4PDayGift = ({ visible, close, user }) => {
  const [loading, setLoading] = useState(false),
    [confirming, setConfirming] = useState(false),
    { t } = useTranslation(),
    dispatch = useDispatch();

  // `visible` is a prop and the early return below does not unmount the component, so the
  // confirm step would still be showing the next time an admin opens this for anyone.
  useEffect(() => {
    if (!visible) setConfirming(false);
  }, [visible]);

  const [badge, setBadge] = useState(null);

  // Award mode: a fan with no enrolment at all. The panel then lets an admin pick a colour and
  // issue the badge by hand, instead of recording a handover there is nothing to hand over for.
  const awarding = canAwardP4PDay(user);
  const [campaign, setCampaign] = useState(null),
    [selected, setSelected] = useState(null);

  // Stock counts come from the admin read, not the public status — the public one deliberately
  // publishes no per-colour stock. Re-read on every open so a colour drained since the last
  // look is already shown as out of stock.
  useEffect(() => {
    if (!visible || !awarding) return undefined;

    let active = true;
    setSelected(null);
    setCampaign(null);
    Api.get('/admin/p4pday/get').then((res) => {
      if (active) setCampaign(res?.data || null);
    });

    return () => {
      active = false;
    };
  }, [visible, awarding]);

  const p4pDay = user?.p4pDay;
  const claimed = !!p4pDay?.giftClaimed;

  // Which physical item this fan's colour is redeemed for. Resolved from the campaign rather
  // than stored on the user, for the same reason the badge's name and colour are: the sponsor
  // can swap what a tier gets, and staff must be told what it gets TODAY.
  //
  // Left in the English the campaign stores. This screen is English throughout, and booth staff
  // are reading it against a printed stock list that says "Key Chain".
  useEffect(() => {
    if (!visible || !p4pDay?.giftColor) return undefined;

    let active = true;
    getP4PDayBadges().then((badges) => {
      if (!active) return;
      setBadge((badges || []).find((b) => b.color === p4pDay.giftColor) || null);
    });

    return () => {
      active = false;
    };
  }, [visible, p4pDay?.giftColor]);

  async function award() {
    setConfirming(false);
    setLoading(true);

    // Not silent: a refusal (out of stock, already enrolled, campaign closed) is toasted with
    // the server's own message, and the call resolves falsy.
    const res = await Api.put('/admin/p4pday/award', { userId: user._id, color: selected });

    setLoading(false);
    if (!res) {
      // Most refusals are a stock change since the panel opened, so show the counts as they are now.
      const fresh = await Api.get('/admin/p4pday/get', null, { silent: true });
      if (fresh?.data) setCampaign(fresh.data);
      setSelected(null);
      return;
    }

    dispatch({
      type: 'ADMIN_EDIT_USER',
      payload: { _id: user._id, p4pDay: res.data.p4pDay }
    });

    const awarded = res.data.p4pDay;
    const tier = (campaign?.campaign?.inventory || []).find(
      (item) => item.color === awarded?.giftColor
    );
    dispatch({
      type: 'HANDLE_RES',
      payload: {
        res: t('admin.p4pDay.award.awarded', {
          color: tier?.colorName || awarded?.giftColor,
          badgeNo: awarded?.badgeNo
        })
      }
    });
  }

  async function mark() {
    setConfirming(false);
    setLoading(true);

    const res = await Api.put('/admin/p4pday/markClaimed', { userId: user._id });

    setLoading(false);
    if (!res) return;

    // ApiResponse wraps every payload in `data` — unlike the older admin endpoints on this
    // screen, which return the changed fields at the top level.
    const result = res.data;

    dispatch({
      type: 'ADMIN_EDIT_USER',
      payload: {
        _id: user._id,
        // Merged rather than replaced: the rest of the enrolment (fan ID, colour, dates) is not
        // in this response and must survive the update.
        p4pDay: {
          ...p4pDay,
          giftClaimed: result.giftClaimed,
          giftClaimedAt: result.giftClaimedAt
        }
      }
    });

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: 'Gift marked as handed over.' }
    });
  }

  if (!visible) return null;

  if (awarding) {
    const inventory = campaign?.campaign?.inventory || [];
    const pick = inventory.find((item) => item.color === selected);
    const soldOut = inventory.length > 0 && inventory.every((item) => item.remaining <= 0);

    let notice = null;
    if (campaign && !campaign.enabled) notice = t('admin.p4pDay.award.switchedOff');
    else if (campaign && !campaign.active) notice = t('admin.p4pDay.award.notActive');
    else if (soldOut) notice = t('admin.p4pDay.award.allSoldOut');

    const canAward = campaign?.active && !soldOut;

    return (
      <ModalV2
        visible={visible}
        closeModal={close}
        width="420px"
        header={
          <Text h4 bold marg="0">
            {t('admin.p4pDay.award.title')}
          </Text>
        }
        footer={
          <Col start wid="100%">
            {confirming && pick ? (
              <>
                <Text small col={theme.GREY_60} marg="0 0 12px" style={{ lineHeight: 1.5 }}>
                  {t('admin.p4pDay.award.confirmText', {
                    color: pick.colorName,
                    username: user.username
                  })}
                </Text>

                <Button
                  small
                  text={t('admin.p4pDay.award.confirmButton')}
                  bg={theme.SECONDARY}
                  onClick={award}
                  disabled={loading}
                  marg="0 0 8px"
                />

                <Button
                  small
                  text={t('admin.p4pDay.award.cancel')}
                  onClick={() => setConfirming(false)}
                />
              </>
            ) : (
              <>
                {canAward && (
                  <Button
                    small
                    text={t('admin.p4pDay.award.awardButton')}
                    bg={theme.SECONDARY}
                    onClick={() => setConfirming(true)}
                    disabled={loading || !pick}
                    marg="0 0 8px"
                  />
                )}

                <Button small text={t('admin.p4pDay.award.close')} onClick={close} />
              </>
            )}
          </Col>
        }
      >
        <Col start wid="100%" pad="20px">
          <Text small col={theme.GREY_60} marg="0 0 16px" style={{ lineHeight: 1.5 }}>
            {t('admin.p4pDay.award.intro')}
          </Text>

          {user?.p4pDay?.revokedAt && (
            <Text small col={theme.GREY_60} marg="0 0 16px" style={{ lineHeight: 1.5 }}>
              {t('admin.p4pDay.award.revokedNote', { date: fmt(user.p4pDay.revokedAt) })}
            </Text>
          )}

          {!campaign && (
            <Text med col={theme.GREY_60}>
              {t('admin.p4pDay.award.loading')}
            </Text>
          )}

          {notice && (
            <Text med col={theme.SECONDARY} marg="0 0 12px">
              {notice}
            </Text>
          )}

          {campaign?.active &&
            inventory.map((item) => {
              const out = item.remaining <= 0;
              return (
                <Option
                  key={item.color}
                  type="button"
                  disabled={out || loading || confirming}
                  selected={selected === item.color}
                  accent={item.colorHex === '#FFFFFF' ? theme.GREY_60 : item.colorHex}
                  onClick={() => setSelected(item.color)}
                >
                  <Row noFlex center>
                    <P4PDayGiftBadge giftColor={item.color} />
                    <Col start marg="0 0 0 12px">
                      <Text med bold col={theme.GREY_90} marg="0">
                        {item.colorName}
                      </Text>
                      <Text small col={theme.GREY_60} marg="0">
                        {item.giftItem}
                      </Text>
                    </Col>
                  </Row>
                  <Text small bold col={out ? theme.SECONDARY : theme.GREY_60} marg="0">
                    {out
                      ? t('admin.p4pDay.award.outOfStock')
                      : t('admin.p4pDay.award.left', {
                          remaining: item.remaining.toLocaleString(),
                          total: item.total.toLocaleString()
                        })}
                  </Text>
                </Option>
              );
            })}
        </Col>
      </ModalV2>
    );
  }

  return (
    <ModalV2
      visible={visible}
      closeModal={close}
      width="420px"
      header={
        <Text h4 bold marg="0">
          P4P Day gift
        </Text>
      }
      footer={
        // The action sits above Close: the panel exists to record a handover, and the button
        // that dismisses it should not be the one under the reader's thumb first.
        //
        // Once marked, the button is gone rather than disabled — a handover cannot be taken
        // back, and leaving a greyed-out control there suggests it could be.
        //
        // The confirmation is a step INSIDE this modal rather than the shared
        // IrreversableModal, which renders through Modal at z-index 11 and so paints behind
        // ModalV2's 1000 — invisible, with its Confirm button swallowed by ModalV2's backdrop.
        <Col start wid="100%">
          {confirming ? (
            <>
              <Text small col={theme.GREY_60} marg="0 0 12px" style={{ lineHeight: 1.5 }}>
                This cannot be undone. Only confirm once the fan has the physical gift in hand.
              </Text>

              <Button
                small
                text="Yes, gift handed over"
                bg={theme.SECONDARY}
                onClick={mark}
                disabled={loading}
                marg="0 0 8px"
              />

              <Button small text="Cancel" onClick={() => setConfirming(false)} />
            </>
          ) : (
            <>
              {!!p4pDay?.giftColor && !claimed && (
                <Button
                  small
                  text="Mark gift handed over"
                  bg={theme.SECONDARY}
                  onClick={() => setConfirming(true)}
                  disabled={loading}
                  marg="0 0 8px"
                />
              )}

              <Button small text="Close" onClick={close} />
            </>
          )}
        </Col>
      }
    >
      <Col start wid="100%" pad="20px">
        <Field label="Fan ID">
          <Code>{p4pDay?.fanId || '—'}</Code>
        </Field>

        <Field label="Badge">
          {p4pDay?.giftColor ? (
            // The real pennant, not the letter. Booth staff match what the fan is holding up on
            // their phone, and the name and colour on it are the sponsor's to change.
            <P4PDayGiftBadge giftColor={p4pDay.giftColor} badgeNo={p4pDay.badgeNo} />
          ) : (
            <Text med col={theme.GREY_60}>
              No badge awarded
            </Text>
          )}
        </Field>

        <Field label="Gift item">
          {p4pDay?.giftColor ? (
            <Text med bold col={theme.GREY_90}>
              {badge?.giftItem || '—'}
            </Text>
          ) : (
            <Text med col={theme.GREY_60}>
              —
            </Text>
          )}
        </Field>

        <Field label="Enrolled via">
          <Text med col={theme.GREY_90}>
            {platformLabel(p4pDay?.platform, p4pDay?.awardedBy)}
          </Text>
        </Field>

        <Field label="Badge awarded">
          <DateValue col={theme.GREY_90}>{fmt(p4pDay?.awardedAt)}</DateValue>
        </Field>

        <Field label="Gift handed over">
          <DateValue col={claimed ? theme.GREEN : theme.GREY_60}>
            {claimed ? fmt(p4pDay?.giftClaimedAt) : 'Not yet'}
          </DateValue>
        </Field>

        {!p4pDay?.giftColor && (
          <Text small col={theme.GREY_60} marg="4px 0 0">
            This fan enrolled but stock had run out, so there is no gift to hand over.
          </Text>
        )}
      </Col>
    </ModalV2>
  );
};

export default P4PDayGift;
